import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { useTracker } from '@archipro-website/tracker';
import { trackEnquiryEngagement } from '~/modules/tracking/util/trackEnquiryEngagement';
import { trackEnquireReveal } from '~/modules/tracking/util/trackEnquireReveal';
import { trackProjectEnquire } from '~/modules/tracking/util/trackProjectEnquiry';
import {
    ENQUIRE_AUTH_SOURCE_VALUE,
    ENQUIRE_MODAL_SHOWUP_KEY,
    ENQUIRE_MODAL_SHOWUP_KEY_VALUE as KEY_VALUE,
} from '~/modules/enquiry/constants';
import {
    AUTH_PROVIDER_PARAM_KEY,
    AUTH_SOURCE_PARAM_KEY,
} from '~/modules/root/hook/use-auth-event';
import {
    enquirySessionAtom,
    type EnquiryProfessional,
    type UseEnquiryProps,
} from './use-enquiry';

type EnquiryModalProfessional = EnquiryProfessional & {
    CompanyLogo: string;
    LogoBackgroundColor: string;
    CustomEnquiryMessage?: string;
    EnquiryMessageOverrideEnabled?: boolean;
    EnquiryMessagePlaceholder?: string;
};

interface EnquiryModalContext {
    /** The title of the modal. */
    title?: string;
    /** The professional to submit an enquiry to. */
    professional: EnquiryModalProfessional;
    /** The type of the Library Item (professional, product, project, article). */
    itemType: UseEnquiryProps['itemType'];
    /** The ID of the Library Item (professional, product, project, article). */
    siteTreeID: UseEnquiryProps['siteTreeID'];
    /** The source of the enquiry. Defaults to `EnquiryModal`. */
    source?: Exclude<UseEnquiryProps['source'], 'InPage'>;
    /** The project details if the enquiry is for a project. Used for tracking. */
    relatedItem?: UseEnquiryProps['relatedItem'];
    /** The CTA text that opened the modal. Used for tracking. */
    ctaLabel?: string;
    /** Include the branch dropdown if the professional has branches. Defaults to `false`. */
    includeBranch?: boolean;
    /** The project ID if the enquiry is for a project. Used for tracking. */
    projectID?: number;
    /** The name of the item being enquired about (e.g. product name). Shown as subtitle in the modal header. */
    itemTitle?: string;
    /** Overrides `itemTitle` for message generation only (e.g. a specific file name). */
    messageSubject?: string;
    /** A thumbnail URL for the item being enquired about. Shown in the modal header. */
    itemImage?: string;
}

export const enquiryModalOpenAtom = atom(false);
const enquiryModalContextAtom = atomWithStorage<EnquiryModalContext | null>(
    'enquiry-modal-context',
    null,
    createJSONStorage(() => sessionStorage),
);

/**
 * Hook to interact with the Enquiry Modal. Can be used to render an Enquiry
 * Modal or open it. Components opening the modal should pass in data about the
 * enquiry (professional, etc.) for the modal to render.
 * @param data Context about the enquiry for the modal to render.
 */
export function useEnquiryModal(data?: EnquiryModalContext) {
    const isAutoOpened = useRef(false);
    const isEngagementTracked = useRef(false);
    const [isOpen, setIsOpen] = useAtom(enquiryModalOpenAtom);
    const [context, setContext] = useAtom(enquiryModalContextAtom);
    const setEnquirySession = useSetAtom(enquirySessionAtom);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const tracker = useTracker();

    const openEnquiryModal = useCallback(
        ({
            title,
            ctaLabel,
            messageSubject,
            addQueryParam = false,
        }: {
            title?: string;
            ctaLabel?: string;
            /** Override the subject used to generate the default enquiry message (e.g. a file name). */
            messageSubject?: string;
            addQueryParam?: boolean;
        } = {}) => {
            const newContext = data
                ? {
                      ...data,
                      ...(title && { title }),
                      ...(ctaLabel && { ctaLabel }),
                      ...(messageSubject && { messageSubject }),
                  }
                : context;
            if (!newContext) {
                return;
            }
            setEnquirySession((prev) => {
                if (!prev) return prev;
                if (
                    messageSubject ||
                    prev.siteTreeId !== newContext.siteTreeID
                ) {
                    return { ...prev, message: undefined };
                }
                return prev;
            });
            const { professional, itemType, siteTreeID, source } = newContext;
            if (data) {
                // Only update context if data is provided.
                setContext(newContext);
            }
            setIsOpen(true);
            isAutoOpened.current = true;

            if (addQueryParam) {
                searchParams.set(ENQUIRE_MODAL_SHOWUP_KEY, KEY_VALUE);
                setSearchParams(searchParams, { preventScrollReset: true });
            }

            trackEnquireReveal(tracker, {
                professionalName: professional.Title,
                itemID: siteTreeID,
                itemType,
                source: source ?? 'EnquiryModal',
                ctaLabel: newContext.ctaLabel,
                projectID: newContext.projectID,
                professionalID: professional.ID,
            });
            if (itemType === 'project' && newContext.relatedItem) {
                trackProjectEnquire(tracker, {
                    category: newContext.relatedItem.category,
                    step: 1,
                    project: newContext.relatedItem.title,
                    professional: professional.Title,
                });
            }
            if (!isEngagementTracked.current) {
                // Track engagement max once per page.
                trackEnquiryEngagement(tracker, {
                    professional: {
                        ID: professional.ID,
                        Title: professional.Title,
                    },
                    itemID: siteTreeID,
                    itemType,
                    source: source ?? 'EnquiryModal',
                });
                isEngagementTracked.current = true;
            }
        },
        [
            tracker,
            searchParams,
            data,
            context,
            setIsOpen,
            setContext,
            setSearchParams,
            setEnquirySession,
        ],
    );

    useEffect(() => {
        // Open the enquiry modal on page load if query param is present.
        if (searchParams.get(ENQUIRE_MODAL_SHOWUP_KEY) !== KEY_VALUE) {
            return;
        }
        // Redirect after social login from enquiry modal, do nothing.
        if (
            searchParams.has(AUTH_PROVIDER_PARAM_KEY) &&
            searchParams.get(AUTH_SOURCE_PARAM_KEY) ===
                ENQUIRE_AUTH_SOURCE_VALUE
        ) {
            return;
        }
        // isAutoOpened is required to prevent infinite loop.
        if (!isAutoOpened.current && data) {
            openEnquiryModal();
        }
    }, [searchParams, data, openEnquiryModal]);

    useEffect(() => {
        if (!data) return;
        const urls = [data.professional.CompanyLogo, data.itemImage].filter(
            (url): url is string => Boolean(url),
        );
        urls.forEach((url) => {
            const img = new window.Image();
            img.src = url;
        });
    }, [data]);

    useEffect(() => {
        isAutoOpened.current = false;
        isEngagementTracked.current = false;
    }, [location.pathname]);

    return {
        /** The open state of the enquiry modal. */
        isOpen,
        /** The current context of the enquiry modal (e.g. professional, item type, etc.). */
        context,
        /**
         * Open the enquiry modal.
         * @param title An optional title of the modal.
         * @param ctaLabel An optional CTA text that opened the modal. Used for tracking.
         * @param addQueryParam Add the query param to the URL so the modal is
         *   auto opened on page load. Defaults to `false`.
         */
        openEnquiryModal,
        /** Close the enquiry modal. */
        closeEnquiryModal: useCallback(() => {
            if (searchParams.has(ENQUIRE_MODAL_SHOWUP_KEY)) {
                searchParams.delete(ENQUIRE_MODAL_SHOWUP_KEY);
                setSearchParams(searchParams, { preventScrollReset: true });
            }
            setIsOpen(false);
        }, [searchParams, setSearchParams, setIsOpen]),
    };
}
